import { Outlet, NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  AlertCircle,
  DollarSign,
  Car,
  LogOut,
  Menu,
  Shield,
  Tag,
  Moon,
  Sun,
  UserCog,
  Settings,
  BarChart2,
  UserCheck,
  CreditCard,
  Megaphone,
  ClipboardList,
  LifeBuoy,
  HelpCircle,
  Bell,
} from 'lucide-react'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/hooks/useTheme'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { getInitials, cn } from '@/lib/utils'
import { NotificationsDropdown } from '@/components/shared/NotificationsDropdown'
import { NotificationBell } from '@/components/shared/NotificationBell'
import { AnnouncementBanner } from '@/components/shared/AnnouncementBanner'
import { usePendingInstructors } from '@/hooks/useInstructorApproval'

const navItems = [
  { to: '/admin',                          label: 'Visão geral',  icon: LayoutDashboard, end: true },
  { to: '/admin/analytics',               label: 'Analytics',    icon: BarChart2 },
  { to: '/admin/instrutores',             label: 'Instrutores',  icon: Users },
  { to: '/admin/instrutores/aprovacao',   label: 'Aprovações',   icon: UserCheck, badgeKey: 'pending' },
  { to: '/admin/disputas',               label: 'Disputas',     icon: AlertCircle },
  { to: '/admin/financeiro',             label: 'Financeiro',   icon: DollarSign },
  { to: '/admin/pagamentos',             label: 'Pagamentos',   icon: CreditCard },
  { to: '/admin/cupons',                 label: 'Cupons',       icon: Tag },
  { to: '/admin/usuarios',               label: 'Usuários',     icon: UserCog },
  { to: '/admin/configuracoes',          label: 'Configurações',icon: Settings },
  { to: '/admin/cupons-promo',          label: 'Promos',       icon: Tag },
  { to: '/admin/anuncios',              label: 'Anúncios',     icon: Megaphone },
  { to: '/admin/auditoria',            label: 'Auditoria',    icon: ClipboardList },
  { to: '/admin/suporte',              label: 'Suporte',       icon: LifeBuoy },
  { to: '/admin/faq',                  label: 'FAQ',           icon: HelpCircle },
  { to: '/admin/notificacoes',         label: 'Notificações',  icon: Bell },
] as const

function Sidebar({ onClose }: { onClose?: () => void }) {
  const { profile, signOut } = useAuth()
  const { data: pendingInstructors } = usePendingInstructors()
  const pendingCount = pendingInstructors?.length ?? 0

  return (
    <div className="flex h-full flex-col bg-gradient-to-b from-brand-700 via-brand-800 to-brand-900">
      {/* Logo */}
      <div className="flex h-16 shrink-0 items-center px-5 border-b border-white/10">
        <img src="/logo.png" alt="MeuDriver" className="h-8 w-auto brightness-0 invert" />
      </div>

      {/* Admin badge */}
      <div className="mx-3 mt-4 rounded-xl bg-white/10 ring-1 ring-white/10 px-3 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-400/20 shrink-0 ring-1 ring-orange-400/20">
            <Shield className="h-4 w-4 text-orange-300" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white truncate leading-tight">
              {profile?.full_name ?? 'Administrador'}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-orange-400" />
              <p className="text-[11px] text-white/50">Administrador</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 pt-5 pb-2 space-y-1">
        <p className="px-3 mb-2 text-[10px] font-semibold tracking-widest uppercase text-white/30">
          Gestão
        </p>
        {navItems.map(({ to, label, icon: Icon, ...rest }) => {
          const end = 'end' in rest ? rest.end : undefined
          const hasBadge = 'badgeKey' in rest && rest.badgeKey === 'pending'
          const badgeCount = hasBadge ? pendingCount : 0
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150',
                  isActive
                    ? 'bg-white text-brand-700 shadow-lg shadow-black/10'
                    : 'text-white/65 hover:bg-white/10 hover:text-white',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={cn(
                      'h-4 w-4 shrink-0 transition-colors',
                      isActive ? 'text-brand-600' : 'text-white/50 group-hover:text-white',
                    )}
                  />
                  <span className="flex-1">{label}</span>
                  {badgeCount > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-white">
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          )
        })}
      </nav>

      {/* Sign out */}
      <div className="px-3 pb-5 border-t border-white/10 pt-3">
        <button
          onClick={signOut}
          className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/40 hover:bg-white/10 hover:text-white/80 transition-all duration-150"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          Sair da conta
        </button>
      </div>
    </div>
  )
}

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { theme, toggleTheme } = useTheme()

  return (
    <div className="flex min-h-screen bg-slate-50/80 dark:bg-gray-950">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:z-50">
        <Sidebar />
      </aside>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 left-0 z-50 w-64 lg:hidden"
            >
              <Sidebar onClose={() => setSidebarOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex flex-1 flex-col lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-slate-200/80 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm px-4 lg:px-6">
          <button
            className="lg:hidden -ml-1 flex h-9 w-9 items-center justify-center rounded-xl hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5 text-slate-600 dark:text-gray-400" />
          </button>

          <div className="flex items-center gap-2 lg:hidden">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-600">
              <Car className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="font-bold text-sm text-slate-900 dark:text-white">MeuDriver</span>
          </div>

          <div className="hidden lg:block">
            <p className="text-sm font-semibold text-slate-500 dark:text-gray-400">Painel Administrativo</p>
          </div>

          <div className="flex-1" />
          <button
            onClick={toggleTheme}
            className="flex h-9 w-9 items-center justify-center rounded-xl hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors text-slate-500 dark:text-gray-400"
            aria-label="Alternar tema"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <NotificationBell />
          <NotificationsDropdown />
        </header>

        <AnnouncementBanner />
        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
